import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress, Autocomplete, TextField } from "@mui/material";
import s3_Upload from "../customComponents/s3_Upload";
import {
  useGetEngageRecipientsQuery,
  useSendEngageMailMutation,
} from "../../api/engageApi";

const SUBJECT_MAX = 140;
const BODY_MAX = 4000;

const toIds = (users) => users.map((user) => user.employeeId);

const RecipientPicker = ({ id, options, value, onChange, loading }) => (
  <Autocomplete
    id={id}
    multiple
    filterSelectedOptions
    options={options}
    getOptionLabel={(option) => option.name || ""}
    isOptionEqualToValue={(option, selected) =>
      option.employeeId === selected.employeeId
    }
    value={value}
    onChange={(event, next) => onChange(next)}
    loading={loading}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        placeholder={value.length ? "" : "Select colleagues..."}
        size="small"
        className="bg-white"
      />
    )}
  />
);

const AdminTriggerMails = ({ onBack }) => {
  const [toRecipients, setToRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: users, isLoading: usersLoading } =
    useGetEngageRecipientsQuery();
  const [sendEngageMail, { isLoading: sending }] = useSendEngageMailMutation();

  // Revokes the previous blob URL whenever preview changes, and on unmount.
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  // employeeId here is the employee mongo id; the server already drops the
  // signed-in user from this list.
  const recipients = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((user) => user.employeeId)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [users]
  );

  // A person can be in To or CC, never both.
  const toOptions = useMemo(() => {
    const taken = new Set(toIds(ccRecipients));
    return recipients.filter((user) => !taken.has(user.employeeId));
  }, [recipients, ccRecipients]);

  const ccOptions = useMemo(() => {
    const taken = new Set(toIds(toRecipients));
    return recipients.filter((user) => !taken.has(user.employeeId));
  }, [recipients, toRecipients]);

  const handleFile = async (event) => {
    const input = event.target;
    const file = input.files && input.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files can be attached");
      input.value = "";
      return;
    }

    setUploading(true);
    let uploaded;
    try {
      uploaded = await s3_Upload(event);
    } finally {
      setUploading(false);
      input.value = "";
    }

    // s3_Upload toasts its own size error and returns undefined.
    if (!uploaded) return;
    if (!uploaded.s3Document) {
      toast.error("Could not upload the image");
      return;
    }

    setPreview(URL.createObjectURL(file));
    setImage({ fileName: file.name, s3Document: uploaded.s3Document });
  };

  const clearImage = () => {
    setPreview("");
    setImage(null);
  };

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const canSubmit =
    toRecipients.length > 0 &&
    Boolean(trimmedSubject) &&
    Boolean(trimmedBody) &&
    !sending &&
    !uploading;

  const handleSend = async () => {
    if (!canSubmit) return;
    try {
      await sendEngageMail({
        toEmployeeIds: toIds(toRecipients),
        ccEmployeeIds: toIds(ccRecipients),
        mailSubject: trimmedSubject,
        mailBody: trimmedBody,
        image: image ? image.s3Document : {},
      }).unwrap();

      toast.success("Email sent successfully");
      onBack();
    } catch (error) {
      toast.error(error?.data?.message || "Could not send the email");
    }
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            disabled={sending}
            aria-label="Close"
            className="rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed"
          >
            &#10005;
          </button>
          <h2 className="flex-1 text-lg font-semibold text-gray-800">
            Send Email
          </h2>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSend}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sending && <CircularProgress size={14} color="inherit" />}
            Send
          </button>
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="mail-to"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            To <span className="text-red-500">*</span>
          </label>
          <RecipientPicker
            id="mail-to"
            options={toOptions}
            value={toRecipients}
            onChange={setToRecipients}
            loading={usersLoading}
          />
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="mail-cc"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            CC
          </label>
          <RecipientPicker
            id="mail-cc"
            options={ccOptions}
            value={ccRecipients}
            onChange={setCcRecipients}
            loading={usersLoading}
          />
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="mail-subject"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            id="mail-subject"
            value={subject}
            maxLength={SUBJECT_MAX}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="What is this about?"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="mail-body"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Body <span className="text-red-500">*</span>
          </label>
          <textarea
            id="mail-body"
            value={body}
            maxLength={BODY_MAX}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Write your message..."
            rows={5}
            className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-right text-xs text-gray-400">
            {body.length}/{BODY_MAX}
          </p>
        </div>

        <div className="px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Image
          </p>
          <p className="mb-3 text-xs text-gray-400">
            Optional. Up to 10 MB, uploaded before the email is sent.
          </p>

          {image ? (
            <div className="flex items-start gap-3">
              {preview && (
                <img
                  src={preview}
                  alt={image.fileName}
                  className="h-24 w-32 flex-none rounded-md border border-gray-200 object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-700">
                  {image.fileName}
                </p>
                <p className="text-xs text-gray-400">Uploaded</p>
                <button
                  type="button"
                  onClick={clearImage}
                  disabled={sending}
                  className="mt-2 text-xs font-semibold text-red-600 underline hover:text-red-700 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              {uploading && <CircularProgress size={14} />}
              {uploading ? "Uploading..." : "Choose an image"}
              <input
                type="file"
                name="image"
                accept="image/*"
                hidden
                disabled={uploading || sending}
                onChange={handleFile}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTriggerMails;
